//import { jwtDecode } from "jwt-decode";
import axiosIns from '@/libs/axios';
import api from '@/libs/api';
import ability from "@/libs/acl/ability.js"
import { getFirebaseBackend } from '../../authUtils.js'
import { getLocalStorage, setLocalStorage, deleteLocalStorage } from '../storage';
import ToastificationContent from "@/components/toastification-content";
import { useToast } from "vue-toastification";
import router from "../../router";
import { hasAccessibleApplicationRoute } from "@/state/helpers";

const toast = useToast();

// Go to the start page (PAS dashboard or home, see libs/landing.js).
// When the user is already on it, router.push does nothing and the page keeps its old data,
// so reload it softly: a new query value changes the router-view key (same trick as switchCurrency).
async function goToStartPage() {
    const before = router.currentRoute.value;

    await router.push('/');

    const after = router.currentRoute.value;
    if (after.fullPath === before.fullPath) {
        await router.replace({
            path: after.path,
            query: { ...after.query, t: Date.now() },
        });
    }
}

export const state = {
    token: getLocalStorage('userJwt'),
    currentUser: getLocalStorage('userData'),
    userGroups: JSON.parse(getLocalStorage('userGroups') || '[]'),
    permissions: JSON.parse(getLocalStorage('userPermissions') || '[]'),
    refreshToken: getLocalStorage('userRefreshToken'),
    currentTenant: JSON.parse(getLocalStorage('currentTenant') || null),
    currentApplication: getLocalStorage('currentApplication') || null,
    availableTenants:  JSON.parse(getLocalStorage('availableTenants') || '[]'),
    tenantApplications:  JSON.parse(getLocalStorage('tenantApplications') || '[]'),
    isLoading: false,
    error: null
}

export const mutations = {
    SET_TOKEN(state, token) {
        state.token = token;
        if (token) {
            setLocalStorage('userJwt', token);
        } else {
            deleteLocalStorage('userJwt');
        }

        // Decode and store permissions
        /*if (token) {
            try {
                const decoded = jwtDecode(token);
            } catch (error) {
                // Clear invalid token from state and storage
                state.token = null;
                state.permissions = [];
                deleteLocalStorage('userJwt');
                deleteLocalStorage('userPermissions');
            }
        }*/
    },
    SET_CURRENT_USER(state, user) {
        state.currentUser = user;
        if (user) {
            setLocalStorage('userData', JSON.stringify(user));
        } else {
            deleteLocalStorage('userData');
        }
    },
    SET_REFRESH_TOKEN(state, refreshToken) {
        state.refreshToken = refreshToken;
        if (refreshToken) {
            setLocalStorage('userRefreshToken', refreshToken);
        } else {
            deleteLocalStorage('userRefreshToken');
        }
    },
    SET_USER_GROUPS(state, userGroups = []) {
        state.userGroups = userGroups;
        setLocalStorage('userGroups', JSON.stringify(userGroups));
    },
    SET_USER_PERMISSIONS(state, permissions = []) {
        state.permissions = permissions;
        setLocalStorage('userPermissions', JSON.stringify(permissions));
    },
    SET_CURRENT_TENANT(state, tenant) {
        state.currentTenant = tenant;
        if (tenant) {
            setLocalStorage('currentTenant', JSON.stringify(tenant));
        } else {
            deleteLocalStorage('currentTenant');
        }
    },
    SET_CURRENT_APPLICATION(state, application) {
      state.currentApplication = application.code;
      if (application) {
        setLocalStorage('currentApplication', application.code);
      } else {
        deleteLocalStorage('currentApplication');
      }
    },
    SET_AVAILABLE_TENANTS(state, tenants) {
        state.availableTenants = tenants;
        setLocalStorage('availableTenants', JSON.stringify(tenants));
    },
    SET_TENANT_APPLICATION(state, tenantApplications) {
      state.tenantApplications = tenantApplications;
      if (tenantApplications) {
        setLocalStorage('tenantApplications', JSON.stringify(tenantApplications));
      } else {
        deleteLocalStorage('tenantApplications');
      }
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    CLEAR_AUTH(state) {
        // Clear state
        state.token = null;
        state.refreshToken = null;
        state.currentUser = null;
        state.currentTenant = null;
        state.currentApplication = null;
        state.userGroups = [];
        state.permissions = [];
        state.availableTenants = [];
        state.tenantApplications = [];
        state.isLoading = false;
        state.error = null;

        deleteLocalStorage('userJwt');
        deleteLocalStorage('userRefreshToken');
        deleteLocalStorage('userData');
        deleteLocalStorage('userGroups');
        deleteLocalStorage('userPermissions');
        deleteLocalStorage('availableTenants');
        deleteLocalStorage('tenantApplications');
    }
}

export const getters = {
    // Whether the user is currently logged in.
    loggedIn(state) {
        return !!state.token
    },
    currentUser(state) {
        return state.currentUser
    },
    currentTenant(state) {
        return state.currentTenant
    },
    currentApplication(state) {
      return state.currentApplication
    },
    isOwner(state) {
        // If user is under owner group, automatically grant all permissions
        const ownerGroups = ['owner'];
        return state.userGroups && state.userGroups.some(group => ownerGroups.includes(group.code));
    },
    // Check if the user has a specific permission
    hasPermission: (state) => (permission) => {
        if (!permission || typeof permission !== 'string') {
            return false;
        }

        const parts = permission.split('.');
        if (parts.length < 2) {
            return false;
        }

        const action = parts.pop();
        const permissionKey = parts.join('.');

        if (!state.permissions || !state.permissions[permissionKey]) {
            return false;
        }
        return state.permissions[permissionKey].includes(action);
    },
    hasAnyPermission: (state, getters) => (permissions) => {
        return permissions.some(p => getters.hasPermission(p));
    }
}

export const actions = {
    // This is automatically run in `src/state/store.js` when the app
    // starts, along with any other actions named `init` in other modules.
    // eslint-disable-next-line no-unused-vars
    init({ commit, dispatch, getters }) {
        let token = getLocalStorage('userJwt');

        if (token) {
            commit("SET_TOKEN", token);
        }

        //if (getters.loggedIn) {
        //
        // }
    },

    // Logs in the current user.
    logIn({ commit, dispatch, getters }, { email, password } = {}) {
        if (getters.loggedIn) return dispatch('validate')

        return new Promise((resolve, reject) => {
            axiosIns.post(api.API_LOGIN, {
                email: email,
                password: password,
            })
            .then(async (response) => {
                const result = response.data.data;
                commit('SET_TOKEN', result.access_token);
                commit('SET_REFRESH_TOKEN', result.refresh_token);

                try {
                    // Context must load first
                    await dispatch('fetchContext');

                    // Config and User data can run concurrently
                    await Promise.all([
                        dispatch('config/fetchConfig', null, { root: true }),
                        dispatch('fetchUserData')
                    ]);
                    resolve(response);
                } catch (error) {
                    // Clear auth state since login technically succeeded but user can't access system
                    commit('CLEAR_AUTH');
                    dispatch('config/clearConfig', null, { root: true });
                    reject(error);
                }
            })
            .catch((error) => {
                reject(error);
            })
        });
    },

    // Logs out the current user.
    logOut({ commit, dispatch }) {
        // eslint-disable-next-line no-unused-vars
        const refreshToken = getLocalStorage('userRefreshToken');
        const token = getLocalStorage('userJwt');

        return new Promise((resolve, reject) => {
            // eslint-disable-next-line no-unused-vars
            axiosIns.post(api.API_LOGOUT, {
                refresh_token: refreshToken
            },
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })
            .then((response) => {
                resolve(response);
            })
            .catch((error) => {
                reject(error);
            })
            .finally(() => {
                commit('CLEAR_AUTH');
                dispatch('config/clearConfig', null, { root: true });
                ability.update([]);
            })
        });
    },

    // register the user
    register({ commit, dispatch, getters }, { username, email, password } = {}) {
        if (getters.loggedIn) return dispatch('validate')

        return getFirebaseBackend().registerUser(username, email, password).then((response) => {
            const user = response
            commit('SET_CURRENT_USER', user)
            return user
        });
    },

    // eslint-disable-next-line no-unused-vars
    resetPassword({ commit, dispatch, getters }, { email } = {}) {
        if (getters.loggedIn) return dispatch('validate')

        return getFirebaseBackend().forgetPassword(email).then((response) => {
            const message = response.data
            return message
        });
    },

    // Validates the current user's token and refreshes it
    // with new data from the API.
    // eslint-disable-next-line no-unused-vars
    validate({ commit, state }) {
        const userDataStorage = getLocalStorage('userData');

        // Check if local storage exists and is a valid JSON string
        if (!userDataStorage) {
            commit('CLEAR_AUTH');
            return Promise.resolve(null);
        }

        let user;
        try {
            user = JSON.parse(userDataStorage);
        } catch (e) {
            // If parsing fails, clear auth and return
            commit('CLEAR_AUTH');
            return Promise.resolve(null);
        }

        if (!state.currentUser) {
            return Promise.resolve(null);
        }

        commit('SET_CURRENT_USER', user);
        return user;
    },

    // Get current user data
    async fetchUserData({ commit }) {
        try {
            const response = await axiosIns.get(api.API_PROFILE);
            const userData = response.data.data;
            commit('SET_CURRENT_USER', userData);

            // ability.update(userData.abilities);

            return response;
        } catch (error) {
            if (error.response) {
                const { status, data } = error.response;

                if (status === 401) {
                    commit('CLEAR_AUTH');
                }

                toast.error({
                    component: ToastificationContent,
                    props: {
                        title: 'Error',
                        text: data.message || 'Failed to fetch profile'
                    }
                });
            }
            throw error;
        }
    },

    // Add tenant context actions
    async fetchContext({ commit }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        
        try {
            const response = await axiosIns.get(api.API_AUTH_CONTEXT);
            const { data } = response.data;
                        
            commit('SET_USER_GROUPS', data.user_groups);
            commit('SET_USER_PERMISSIONS', data.permissions);
            commit('SET_CURRENT_TENANT', data.current_tenant);
            commit('SET_AVAILABLE_TENANTS', data.available_tenants);

          const tenantApplications =
            data.tenant_applications.map(app => {
              const accessibleRoute = !!hasAccessibleApplicationRoute(app.code);

              return {
                ...app,
                accessible: accessibleRoute,
              };
            });

            commit('SET_TENANT_APPLICATION', tenantApplications);

            return data;
        } catch (error) {
            commit('SET_ERROR', error.message);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async switchTenant({ commit, state, dispatch }, tenant) {
        if (!state.availableTenants.some(availableTenant => availableTenant.id === tenant.id)) {
            throw new Error('Invalid tenant ID');
        }

        try {
            commit('SET_CURRENT_TENANT', tenant);

            // Refresh context to get updated permissions, so the start page is picked with the new tenant's access
            await dispatch('fetchContext').catch(() => {});

            await goToStartPage();
        } catch (error) {
            console.error('Failed to switch tenant:', error);
            throw error;
        }
    },

    async switchApplication({ commit, state }, application) {
      if (!state.tenantApplications.some(tenantApplications => tenantApplications.id === application.id)) {
        throw new Error('Invalid application ID');
      }

      try {
        commit('SET_CURRENT_APPLICATION', application);

        // the start page decides: PAS dashboard for PAS users who may view it, home page otherwise
        router.push('/');
      } catch (error) {
        console.error('Failed to switch application:', error);
        throw error;
      }
    },

    // Forgot password action
    async forgotPassword({ commit }, { email }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        
        try {
            const response = await axiosIns.post(api.API_FORGOT_PASSWORD, {
                email: email
            });
            
            return response.data;
        } catch (error) {
            commit('SET_ERROR', error.message);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    // Check whether a password reset link is still valid
    async checkResetToken({ commit }, { token, email }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        try {
            const response = await axiosIns.post(api.API_CHECK_RESET_TOKEN, {
                token: token,
                email: email
            });

            return response.data;
        } catch (error) {
            commit('SET_ERROR', error.message);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    // Reset password action
    async resetPasswordWithToken({ commit }, { token, email, password, passwordConfirmation }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        
        try {
            const response = await axiosIns.post(api.API_RESET_PASSWORD, {
                token: token,
                email: email,
                password: password,
                password_confirmation: passwordConfirmation
            });
            
            return response.data;
        } catch (error) {
            commit('SET_ERROR', error.message);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },
}
